import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router'

import { consulta, pedir } from '../api/nucleo'
import type { Permisos } from '../api/capacidades'
import type { Lista } from '../api/tipos'
import { Aviso, Boton, Tarjeta } from '../componentes/ui'
import { Editor } from '../motor/Editor'
import { Listado } from '../motor/Listado'
import { Modal } from '../motor/campos/Medio'
import { abandonoComoLista } from './tiendaEnLlano'

/**
 * Las respuestas de los formularios (CLAUDE.md §9): el listado con filtro por formulario y
 * por estado, exportar a CSV o JSON, las métricas con el **abandono por paso**, y la
 * ficha de cada respuesta con lo que respondió el visitante, sus adjuntos con enlace
 * firmado, el estado y las notas internas. Exportar queda en la auditoría: son datos
 * personales de terceros.
 *
 * Para el cliente son «Respuestas» (en `/respuestas`): «Envíos» se confundía con los envíos
 * de la tienda. La colección del núcleo sigue siendo `envios`, y de ahí el nombre del fichero
 * y de sus rutas de la API.
 */
type Formulario = { id: number; nombre: string }
// `abandonoPorPaso` es una lista; un núcleo anterior la mandaba como objeto `{ "1": n }` y el `.map` tumbaba el panel entero.
type Metricas = { formulario: number; completos: number; spam: number; parciales: number; nuevos: number; pasos: number; abandonoPorPaso: number[] | Record<string, number>; nombresDePaso?: string[] }

const useFormularios = (sitio: number) =>
  useQuery({
    queryKey: ['formularios-nombres', sitio],
    queryFn: async () => (await pedir<Lista<Formulario>>(`/formularios${consulta({ 'where[sitio][equals]': sitio, limit: 100, depth: 0, sort: 'nombre' })}`)).docs,
  })

const PanelDeMetricas = ({ espacio, formulario, cerrar }: { cerrar: () => void; espacio: number; formulario: Formulario }) => {
  const { data, error } = useQuery({
    queryKey: ['envios-metricas', espacio, formulario.id],
    queryFn: () => pedir<Metricas>(`/envios/metricas${consulta({ cliente: espacio, formulario: formulario.id })}`),
  })
  return (
    <Modal ancho="max-w-xl" cerrar={cerrar} titulo={`Métricas · ${formulario.nombre}`}>
      {error ? (
        <Aviso tono="error">No se han podido leer las métricas.</Aviso>
      ) : !data ? (
        <p className="text-tenue">Calculando…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Completas', data.completos],
              ['Nuevas', data.nuevos],
              ['A medias', data.parciales],
              ['Spam', data.spam],
            ].map(([e, v]) => (
              <Tarjeta className="!p-3" key={String(e)}>
                <p className="text-xs text-tenue">{e}</p>
                <p className="font-display text-2xl font-semibold">{v}</p>
              </Tarjeta>
            ))}
          </div>
          {data.pasos > 1 && (
            <div className="mt-5">
              <p className="mb-2 text-sm font-medium">Abandono por paso</p>
              <p className="mb-3 text-xs text-tenue">Cuántos se quedaron a medias en cada paso. Es lo que de verdad dice qué pregunta sobra.</p>
              <ol className="flex flex-col gap-2">
                {abandonoComoLista(data.abandonoPorPaso).map((n, i, lista) => {
                  const maximo = Math.max(1, ...lista)
                  return (
                    <li className="flex items-center gap-3 text-sm" key={i}>
                      <span className="w-28 truncate text-tenue" title={data.nombresDePaso?.[i]}>
                        {data.nombresDePaso?.[i] ?? `Paso ${i + 1}`}
                      </span>
                      <span className="h-3 flex-1 overflow-hidden rounded-boton bg-papel-3">
                        <span className="block h-full bg-naranja" style={{ width: `${(n / maximo) * 100}%` }} />
                      </span>
                      <span className="w-8 text-right tabular-nums">{n}</span>
                    </li>
                  )
                })}
              </ol>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}

/** Descargar las respuestas de un formulario, con el filtro de estado que haya puesto (queda en la auditoría: son datos de personas). */
const exportarRespuestas = (espacio: number, formulario: number, formato: 'csv' | 'json', estado: null | string) => {
  // La zona horaria de quien exporta: las fechas del CSV salen en su hora, no en la del servidor.
  const zona = Intl.DateTimeFormat().resolvedOptions().timeZone
  window.open(`/api/envios/exportar${consulta({ cliente: espacio, formulario, formato, ...(estado ? { estado } : {}), ...(zona ? { zona } : {}) })}`, '_blank')
}

/**
 * Las respuestas de **un** formulario, dentro de su constructor (pestaña «Respuestas»): el
 * mismo listado de Respuestas, ya filtrado por él, con sus estados, exportar y las métricas
 * con el abandono por paso. La búsqueda y los filtros viven en la pantalla, no en la
 * dirección: el constructor no se mueve. Cada respuesta abre su ficha en Respuestas.
 */
export const RespuestasDeUnFormulario = ({ espacio, sitio, formulario }: { espacio: number; formulario: Formulario; sitio: number }) => {
  const [parametros, setParametros] = useState(() => new URLSearchParams())
  const [metricas, setMetricas] = useState(false)
  const respuestas = `/e/${espacio}/s/${sitio}/respuestas`
  return (
    <>
      <Listado
        accion={
          <>
            <Boton onClick={() => exportarRespuestas(espacio, formulario.id, 'csv', parametros.get('f_estado'))} pequeno>
              Exportar CSV
            </Boton>
            <Boton onClick={() => exportarRespuestas(espacio, formulario.id, 'json', parametros.get('f_estado'))} pequeno>
              JSON
            </Boton>
            <Boton onClick={() => setMetricas(true)} pequeno>
              Métricas
            </Boton>
          </>
        }
        base={respuestas}
        coleccion="envios"
        columnas={['resumen', 'estado', 'fecha']}
        compacto
        descripcion="Lo que ha respondido la gente en este formulario. Abre una para leerla entera, cambiar su estado o apuntar una nota."
        espacio={espacio}
        parametros={{ valor: parametros, poner: setParametros }}
        puedeCrear={false}
        sitio={sitio}
        vacio="Cuando alguien rellene este formulario en tu web, su respuesta aparecerá aquí, y te llegará un aviso por correo."
        where={{ 'where[formulario][equals]': String(formulario.id) }}
      />
      {metricas && <PanelDeMetricas cerrar={() => setMetricas(false)} espacio={espacio} formulario={formulario} />}
    </>
  )
}

export const ListaDeEnvios = ({ espacio, sitio, base, descripcion, vacio }: { base: string; descripcion?: string; espacio: number; sitio: number; vacio?: string }) => {
  const { data: formularios } = useFormularios(sitio)
  const [params] = useSearchParams()
  const [metricas, setMetricas] = useState(false)
  const elegido = formularios?.find((f) => String(f.id) === params.get('f_formulario')) ?? null

  const exportar = (formato: 'csv' | 'json') => {
    if (!elegido) return
    exportarRespuestas(espacio, elegido.id, formato, params.get('f_estado'))
  }

  return (
    <>
      <Listado
        accion={
          <>
            <Boton disabled={!elegido} onClick={() => exportar('csv')} pequeno title={elegido ? undefined : 'Elige un formulario en el filtro para exportar'}>
              Exportar CSV
            </Boton>
            <Boton disabled={!elegido} onClick={() => exportar('json')} pequeno title={elegido ? undefined : 'Elige un formulario en el filtro'}>
              JSON
            </Boton>
            <Boton disabled={!elegido} onClick={() => setMetricas(true)} pequeno title={elegido ? undefined : 'Elige un formulario en el filtro'}>
              Métricas
            </Boton>
          </>
        }
        base={base}
        coleccion="envios"
        columnas={['resumen', 'formulario', 'estado', 'fecha']}
        descripcion={descripcion}
        espacio={espacio}
        filtrosExtra={[{ clave: 'formulario', etiqueta: 'Formulario', opciones: (formularios ?? []).map((f) => ({ valor: String(f.id), etiqueta: f.nombre })) }]}
        puedeCrear={false}
        sitio={sitio}
        vacio={vacio ?? 'Cuando alguien rellene un formulario de tu web, su respuesta aparecerá aquí.'}
      />
      {metricas && elegido && <PanelDeMetricas cerrar={() => setMetricas(false)} espacio={espacio} formulario={elegido} />}
    </>
  )
}

type Archivo = { campo?: string; nombre?: string; clave?: string; tamano?: number; tipo?: string }

const Adjunto = ({ espacio, envio, indice, archivo }: { archivo: Archivo; envio: number; espacio: number; indice: number }) => {
  const [enlace, setEnlace] = useState<null | string>(null)
  const [error, setError] = useState<null | string>(null)
  return (
    <li className="flex flex-wrap items-center gap-2 text-sm">
      <span>{archivo.nombre ?? `Adjunto ${indice + 1}`}</span>
      {enlace ? (
        <a className="text-violeta underline" href={enlace} rel="noreferrer" target="_blank">
          Descargar (10 minutos)
        </a>
      ) : (
        <Boton
          onClick={() => {
            setError(null)
            pedir<{ url: string }>(`/envios/archivo${consulta({ cliente: espacio, envio, indice })}`)
              .then((r) => setEnlace(r.url))
              .catch((e: Error) => {
                setEnlace(null)
                setError(e.message)
              })
          }}
          pequeno
          tipo="discreto"
        >
          Pedir enlace
        </Boton>
      )}
      {error && (
        <span className="basis-full text-xs text-error" role="alert">
          {error}
        </span>
      )}
    </li>
  )
}

type FilaLegible = { clave: string; etiqueta: string; texto: string }

/**
 * Lo que respondió, como lo lee una persona: la etiqueta de cada campo, la opción con su
 * texto, «Sí», las fechas de aquí. Lo pone el núcleo (`/envios/legible`), con la misma
 * función que el correo del aviso y el CSV; si no contesta, se enseña lo guardado tal cual.
 */
const Valores = ({ espacio, envio, valores }: { envio: number; espacio: number; valores: unknown }) => {
  const { data, isPending } = useQuery({
    queryKey: ['envio-legible', espacio, envio],
    queryFn: () => pedir<{ filas: FilaLegible[] }>(`/envios/legible${consulta({ cliente: espacio, envio })}`),
  })
  if (isPending) return <p className="text-sm text-tenue">Leyendo…</p>
  const filas: FilaLegible[] =
    data?.filas ??
    (valores && typeof valores === 'object' ? Object.entries(valores as Record<string, unknown>).map(([clave, v]) => ({ clave, etiqueta: clave, texto: typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—') })) : [])
  if (filas.length === 0) return <p className="text-tenue">Sin respuestas.</p>
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[minmax(8rem,auto)_1fr]">
      {filas.map((f) => (
        <div className="contents" key={f.clave}>
          <dt className="font-medium text-tinta-2">{f.etiqueta}</dt>
          <dd className="break-words whitespace-pre-wrap">{f.texto}</dd>
        </div>
      ))}
    </dl>
  )
}

export const FichaDeEnvio = ({ espacio, sitio, base, permisos }: { base: string; espacio: number; permisos: Permisos; sitio: number }) => {
  const { id } = useParams()
  const envio = Number(id)
  const [mensaje, setMensaje] = useState<null | { texto: string; tono: 'aviso' | 'error' | 'ok' }>(null)
  // Reenviar el aviso es de quien edita los formularios (el núcleo pide `formularios.editar`).
  const puedeReenviar = permisos.tiene('formularios.editar')
  return (
    <Editor
      acciones={() =>
        puedeReenviar ? (
          <Boton
            onClick={() => {
              pedir<{ enviados: number }>(`/envios/reenviar${consulta({ cliente: espacio, envio })}`, { metodo: 'POST', cuerpo: {} })
                .then((r) =>
                  setMensaje(
                    r.enviados > 0
                      ? { texto: `Aviso reenviado a ${r.enviados === 1 ? 'un destinatario' : `${r.enviados} destinatarios`}.`, tono: 'ok' }
                      : { texto: 'No se ha avisado a nadie: este formulario no tiene a quién. Añade un aviso en el formulario («Qué pasa al enviar») o pon un correo de contacto en Ajustes.', tono: 'aviso' },
                  ),
                )
                .catch((e: Error) => setMensaje({ texto: e.message, tono: 'error' }))
            }}
            pequeno
          >
            Reenviar el aviso
          </Boton>
        ) : null
      }
      base={base}
      cabecera={({ valores }) => (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
          <Tarjeta>
            <p className="mb-3 text-xs font-medium tracking-wide text-tenue uppercase">Lo que respondió</p>
            <Valores envio={envio} espacio={espacio} valores={valores.valores} />
          </Tarjeta>
          <Tarjeta>
            <p className="mb-3 text-xs font-medium tracking-wide text-tenue uppercase">Adjuntos</p>
            {Array.isArray(valores.archivos) && valores.archivos.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {(valores.archivos as Archivo[]).map((a, i) => (
                  <Adjunto archivo={a} envio={envio} espacio={espacio} indice={i} key={i} />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-tenue">Ninguno.</p>
            )}
            {mensaje && (
              <div className="mt-3">
                <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>
              </div>
            )}
          </Tarjeta>
        </div>
      )}
      coleccion="envios"
      espacio={espacio}
      id={envio}
      ocultarCampos={['valores', 'archivos', 'resumen']}
      sitio={sitio}
    />
  )
}
